import { queryOptions, useMutation } from "@tanstack/react-query";

import { env } from "@/env";
import {
  http,
  type RequestOptions,
  type HttpResponse,
} from "@/shared/api/client";

type ResetPasswordFinish = {
  key: string;
  newPassword: string;
};

const resetKeys = {
  all: ["gateway", "reset"] as const,
  resetKey: (resetKey: string) =>
    [...resetKeys.all, "reset-key", resetKey] as const,
};

const resetKeyQuery = (resetKey: string) =>
  queryOptions({
    queryKey: resetKeys.resetKey(resetKey),
    queryFn: async ({ signal }) => {
      const response = await checkResetKey(resetKey, { signal });
      return response.data;
    },
    retry: false,
  });

// silent: an unknown address must look exactly like a known one, so the caller
// shows "mail sent" either way and a failure toast would give the answer away
const useResetInitMutation = () =>
  useMutation({
    mutationFn: (email: string) => resetPasswordInit(email),
    meta: { silent: true, skipInvalidation: true },
  });

// silent: the caller treats failure as "reset key no longer valid" and renders that state inline
const useResetFinishMutation = () =>
  useMutation({
    mutationFn: (payload: ResetPasswordFinish) => resetPasswordFinish(payload),
    meta: { silent: true, skipInvalidation: true },
  });

const checkResetKey = (
  resetKey: string,
  config?: RequestOptions,
): Promise<HttpResponse<boolean>> =>
  http.get<boolean>(accountsUrl("/check-reset-key"), {
    ...config,
    params: { resetKey },
    skipFarmId: true,
  });

const resetPasswordInit = (email: string): Promise<HttpResponse<void>> =>
  http.post<void>(accountsUrl("/reset-password/init"), undefined, {
    params: { mail: email.trim() },
    skipFarmId: true,
  });

const resetPasswordFinish = (
  payload: ResetPasswordFinish,
): Promise<HttpResponse<void>> =>
  http.post<void>(accountsUrl("/reset-password/finish"), payload, {
    skipFarmId: true,
  });

const accountsUrl = (path: string): string =>
  `${env.GATEWAY_URL}/accounts${path}`;

export {
  resetKeys,
  resetKeyQuery,
  useResetInitMutation,
  useResetFinishMutation,
  type ResetPasswordFinish,
};
